import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { setActivityApi } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const api = useCallback(() => {
    const instance = axios.create({
      baseURL: API,
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    // Auto-logout on expired token
    instance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          const detail = error.response?.data?.detail || '';
          if (detail.includes('expirado') || detail.includes('expired')) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setToken(null);
            setUser(null);
          }
        }
        return Promise.reject(error);
      }
    );
    return instance;
  }, [token]);

  // Set up activity tracking API
  useEffect(() => {
    if (token) {
      setActivityApi(api);
    }
  }, [token, api]);

  // Function to refresh user data from server
  const refreshUser = useCallback(async () => {
    const storedToken = localStorage.getItem('token');
    if (!storedToken) return;
    
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${storedToken}` }
      });
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
    } catch (error) {
      if (error.response?.status === 401) {
        console.warn('Session expired during refresh');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setToken(null);
        setUser(null);
        return;
      }
      console.error('Error refreshing user:', error);
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      
      if (storedToken && storedUser) {
        // Validate token FIRST before setting state
        // This prevents polling with an expired token
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` }
          });
          // Token is valid - now set state
          setToken(storedToken);
          setUser(response.data);
          localStorage.setItem('user', JSON.stringify(response.data));
        } catch (error) {
          console.error('Token validation failed, clearing session:', error.message);
          // Token is expired/invalid - clear everything silently
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  // Refresh user permissions periodically (every 1 minute) and on navigation
  // This ensures permission changes take effect without requiring re-login
  useEffect(() => {
    if (!token) return;

    const refreshPermissions = async () => {
      try {
        const response = await axios.get(`${API}/auth/my-permissions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const newPermissions = [...(response.data.permissions || [])].sort();
        
        // Use functional state update to avoid stale closure issues
        setUser(prevUser => {
          if (!prevUser) return prevUser; // Don't update if user isn't loaded yet
          
          const currentPermissions = [...(prevUser.permissions || [])].sort();
          
          if (JSON.stringify(newPermissions) !== JSON.stringify(currentPermissions) || prevUser.role !== response.data.role) {
            const updatedUser = { ...prevUser, permissions: response.data.permissions, role: response.data.role };
            localStorage.setItem('user', JSON.stringify(updatedUser));
            return updatedUser;
          }
          return prevUser;
        });
      } catch (error) {
        // If token is expired/invalid, auto-logout
        if (error.response?.status === 401) {
          console.warn('Permission refresh: token expired, logging out');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setToken(null);
          setUser(null);
          return;
        }
        console.debug('Permission refresh failed:', error.message);
      }
    };

    // Delay first refresh to let initAuth complete first
    const initialTimeout = setTimeout(refreshPermissions, 2000);

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refreshPermissions();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    const interval = setInterval(refreshPermissions, 60 * 1000);
    
    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [token]);

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { username, password });
      const { token: newToken, user: userData } = response.data;
      
      setToken(newToken);
      setUser(userData);
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(userData));
      
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al iniciar sesión';
      return { success: false, error: message };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  const isAdmin = () => {
    return user?.role === 'admin' || user?.role === 'superadmin' || user?.role === 'supervisor';
  };

  const isSuperAdmin = () => {
    return user?.role === 'superadmin';
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    // Superadmin has all permissions
    if (user.role === 'superadmin') return true;
    // Check if user has the specific permission
    return user.permissions?.includes(permission) ?? false;
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    isAdmin,
    isSuperAdmin,
    hasPermission,
    api,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
